import dayjs from "dayjs";

import { DEFAULT_WHATSAPP_BUSINESS_ACCOUNT_ID } from "src-next/modules/crm-clinical/messageTemplates/constants";
import { messageTemplatesService } from "src-next/modules/crm-clinical/messageTemplates/services/messageTemplatesService";
import { filterMessageTemplatesByOrganization } from "src-next/modules/crm-clinical/messageTemplates/utils/messageTemplateUiUtils";
import {
  filterBirthdayToday,
  filterPatientsWithPhone,
} from "src-next/modules/crm-clinical/communications/aggregations";
import type { CrmPatientSummary } from "src-next/modules/crm-clinical/communications/types";
import { mapCrmPatientSummaryToMessagingPatient } from "src-next/modules/crm-clinical/communications/helpers/mapCrmPatientSummaryToMessagingPatient";
import { resolveMessagingTutorsFromCrmPets } from "src-next/modules/crm-clinical/communications/helpers/resolveMessagingTutorsFromCrmPets";
import { runSendManyWhatsAppMessagesOrchestrator } from "src-next/modules/crm-clinical/messaging/helpers/sendWhatsAppMessageOrchestrator";
import { doesTemplateRequireQuestionnaire } from "src-next/modules/crm-clinical/messaging/helpers/templateQuestionnaireHelpers";
import { endpointService } from "src-next/modules/crm-clinical/messaging/services/endpointService";
import type {
  MessagingPatient,
  SendMessageResult,
} from "src-next/modules/crm-clinical/messaging/types";
import { isVetApp } from "src-next/modules/crm-clinical/messaging/helpers/isVetApp";
import { crmMessageFranchiseStatsService } from "src-next/modules/crm-clinical/statistics/services/crmMessageFranchiseStatsService";
import { questionnaireManagementService } from "src-next/modules/questionnaire-builder/questionnaire-management/services";

import {
  buildAutomationContext,
  buildMessagingAppointment,
  buildQuestionnaireOptionFromTemplate,
  findAutomationMessageTemplateForSend,
  logAutomationTriggerError,
  logAutomationTriggerSkip,
  mapMessageTemplateToMessagingTemplate,
  normalizeAutomationOrganizationId,
  resolveFluxzapRequesterReference,
  type AutomationTriggerSkipResult,
} from "../helpers/automationMessagingTriggerHelpers";
import { automationService } from "./automationService";
import type { AutomationConfig, AutomationContext } from "../types";

export type TriggerBirthdayAutomationParams = {
  context: AutomationContext;
  requesterReference?: string | null;
  /** Pacientes da organização (serão filtrados para aniversariantes do dia). */
  allPatients: CrmPatientSummary[];
  /** Data de referência; padrão = hoje. */
  refDate?: Date;
};

export type TriggerBirthdayAutomationResult =
  | SendMessageResult
  | AutomationTriggerSkipResult;

/**
 * Disparo em lote da automação de aniversariantes.
 * Usa appointment ad-hoc (sem consulta vinculada) — paridade com CRM communications.
 */
export async function triggerBirthdayAutomationBatch(
  params: TriggerBirthdayAutomationParams,
): Promise<TriggerBirthdayAutomationResult> {
  const organizationId = normalizeAutomationOrganizationId(
    params.context.organizationId,
  );
  if (!organizationId) {
    const reason = "organizationId ausente para automação de aniversariantes.";
    logAutomationTriggerSkip(reason);
    return { status: "skipped", reason };
  }

  const automationContext = buildAutomationContext({
    organizationId,
    practitionerId: params.context.practitionerId,
    whatsappBusinessAccountId: params.context.whatsappBusinessAccountId,
  });
  if (!automationContext) {
    const reason = "Contexto de automação incompleto (org/profissional).";
    logAutomationTriggerSkip(reason);
    return { status: "skipped", reason };
  }

  const requesterReference = resolveFluxzapRequesterReference(
    params.requesterReference,
  );
  if (!requesterReference) {
    const reason = "requesterReference (PractitionerRole) não configurado.";
    logAutomationTriggerSkip(reason);
    return { status: "skipped", reason };
  }

  const refDate = params.refDate ?? new Date();
  const birthdayPatients = filterBirthdayToday(params.allPatients, refDate);
  const eligiblePatients = filterPatientsWithPhone(birthdayPatients);

  if (eligiblePatients.length === 0) {
    const reason =
      birthdayPatients.length === 0
        ? "Nenhum aniversariante hoje na organização."
        : "Nenhum aniversariante hoje com telefone cadastrado.";
    logAutomationTriggerSkip(reason);
    return { status: "skipped", reason };
  }

  const automationItems = await automationService.listByContext({
    context: automationContext,
  });
  const automationItem = automationItems.find((item) => item.id === "birthday");
  if (!automationItem?.isConfigured || !automationItem.isEnabled) {
    const reason =
      'Automação "birthday" não configurada ou desabilitada.';
    logAutomationTriggerSkip(reason);
    return { status: "skipped", reason };
  }

  const config = await automationService.getConfigByAutomationId({
    context: automationContext,
    automationId: "birthday",
  });
  if (!config?.messageTemplateId) {
    const reason = 'Automação "birthday" sem template configurado.';
    logAutomationTriggerSkip(reason);
    return { status: "skipped", reason };
  }

  const [endpointConfig, activeEndpoint] = await Promise.all([
    endpointService.getWhatsAppEndpointConfigByContext({ organizationId }),
    endpointService.getActiveWhatsAppEndpointByContext({ organizationId }),
  ]);
  const hasActiveMetaEndpoint = Boolean(
    activeEndpoint?.id && activeEndpoint.status === "active",
  );
  const requiresFranchiseGate = Boolean(organizationId) && !hasActiveMetaEndpoint;

  if (requiresFranchiseGate) {
    const franchiseStats =
      await crmMessageFranchiseStatsService.fetchCrmMessageFranchiseUsageStats(
        organizationId,
      );
    if ((franchiseStats.remainingMessagesForSend ?? 0) < eligiblePatients.length) {
      const reason =
        "Saldo de mensagens CRM insuficiente para envio automático de aniversariantes.";
      logAutomationTriggerSkip(reason);
      return { status: "skipped", reason };
    }
  }

  const wabaId =
    endpointConfig.wabaIdRank1?.trim() ||
    params.context.whatsappBusinessAccountId?.trim() ||
    DEFAULT_WHATSAPP_BUSINESS_ACCOUNT_ID;

  const [rawTemplates, questionnaires] = await Promise.all([
    messageTemplatesService.list({ whatsappBusinessAccountId: wabaId }),
    questionnaireManagementService.listByOrganizationIdentifier(organizationId),
  ]);

  const orgTemplates = filterMessageTemplatesByOrganization(
    rawTemplates,
    organizationId,
  );
  const messageTemplate = findAutomationMessageTemplateForSend({
    templates: orgTemplates,
    messageTemplateId: config.messageTemplateId,
    organizationId,
  });
  if (!messageTemplate) {
    const reason = `Template "${config.messageTemplateId}" não encontrado ou inelegível para envio.`;
    logAutomationTriggerSkip(reason);
    return { status: "skipped", reason };
  }

  const messagingTemplate = mapMessageTemplateToMessagingTemplate(messageTemplate);
  const questionnaireNameById = new Map(
    questionnaires
      .filter((item) => Boolean(item.id))
      .map((item) => [item.id as string, item.title]),
  );
  const questionnaireOption = buildQuestionnaireOptionFromTemplate({
    template: messageTemplate,
    questionnaireNameById,
  });

  if (
    doesTemplateRequireQuestionnaire(messagingTemplate) &&
    !questionnaireOption?.id
  ) {
    const reason =
      "Template exige questionário, mas nenhum questionário vinculado foi encontrado.";
    logAutomationTriggerSkip(reason);
    return { status: "skipped", reason };
  }

  let messagingPatients: MessagingPatient[];
  try {
    if (isVetApp()) {
      messagingPatients = await resolveMessagingTutorsFromCrmPets(eligiblePatients);
    } else {
      messagingPatients = eligiblePatients.map((p) =>
        mapCrmPatientSummaryToMessagingPatient(p),
      );
    }
  } catch (error) {
    logAutomationTriggerError(
      "Erro ao resolver destinatários para aniversariantes.",
      error,
    );
    return {
      status: "error",
      errorMessage: "Erro ao resolver destinatários para envio.",
    };
  }

  const withPhone = messagingPatients.filter((p) => Boolean(p.phone?.trim()));
  if (withPhone.length === 0) {
    const reason = "Nenhum destinatário com telefone válido para envio.";
    logAutomationTriggerSkip(reason);
    return { status: "skipped", reason };
  }

  // Appointment ad-hoc: envios CRM sem consulta vinculada (paridade communications).
  const appointment = buildMessagingAppointment({
    appointmentId: "ad-hoc",
    startAtIso: dayjs(refDate).toISOString(),
  });

  try {
    const result = await runSendManyWhatsAppMessagesOrchestrator(
      {
        patients: withPhone,
        appointment,
        template: messagingTemplate,
        questionnaire: questionnaireOption,
        requesterReference,
        automationConfig: config as AutomationConfig,
      },
      {
        wabaIdRank1: endpointConfig.wabaIdRank1,
        phoneNumberIdRank1: endpointConfig.phoneNumberIdRank1,
      },
    );

    if (result.status === "error") {
      logAutomationTriggerError(
        `Falha no envio automático de aniversariantes: ${result.errorMessage}`,
      );
    }

    return result;
  } catch (error) {
    logAutomationTriggerError(
      "Erro inesperado no envio automático de aniversariantes.",
      error,
    );
    return {
      status: "error",
      errorMessage: "Erro ao enviar mensagens de aniversário.",
    };
  }
}
